import type {
  AiProgress,
  ApiEnvelope,
  AuthUser,
  BannedTag,
  BannedTagInput,
  DeletionDecision,
  DeletionPage,
  DeletionStatus,
  DeletionSummary,
  ImageDetail,
  ImageFeedPage,
  ImagePatch,
  MyUploadsPage,
  Rating,
  UploadResult,
} from './types'

const API = '/api/v1'

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

function messageFrom(body: Partial<ApiEnvelope<unknown>> | null, status: number): string {
  const text = body?.messages?.find((m) => m?.text)?.text
  if (text) return text
  if (status === 401) return 'Please sign in to continue.'
  if (status === 403) return 'You are not allowed to do that.'
  if (status === 404) return 'Not found.'
  if (status === 413) return 'The file is too large.'
  if (status === 415) return 'This file type is not supported.'
  if (status === 429) return 'Too many requests – please wait a moment.'
  if (status === 451) return 'This image is not available.'
  return `Request failed (${status || 'network error'}).`
}

/** Unwraps the backend's `{ data, success, messages }` envelope. */
export function unwrap<T>(status: number, ok: boolean, body: Partial<ApiEnvelope<T>> | null): T {
  if (!ok || !body || body.success === false) {
    throw new ApiError(messageFrom(body, status), status)
  }
  return body.data as T
}

/**
 * Query retry policy: client errors (401, 403, 404, 451 …) and 503 won't
 * change on retry, so only network and server errors are retried.
 */
export function shouldRetry(failureCount: number, error: unknown, maxRetries = 1): boolean {
  const status = (error as { status?: number } | null)?.status ?? 0
  if ((status >= 400 && status < 500) || status === 503) return false
  return failureCount < maxRetries
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')

  let res: Response
  try {
    res = await fetch(path, { credentials: 'include', ...init, headers })
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    throw new ApiError('Network error – check your connection.', 0)
  }

  let body: Partial<ApiEnvelope<T>> | null
  try {
    body = (await res.json()) as Partial<ApiEnvelope<T>>
  } catch {
    body = null
  }
  return unwrap<T>(res.status, res.ok, body)
}

export const api = {
  images(params: { q?: string; cursor?: string; unsafe?: boolean }, signal?: AbortSignal) {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.cursor) qs.set('n', params.cursor)
    qs.set('r', params.unsafe ? 'true' : 'false')
    return request<ImageFeedPage>(`${API}/imgb?${qs}`, { signal })
  },

  image(id: string, signal?: AbortSignal) {
    return request<ImageDetail>(`${API}/imgb/${encodeURIComponent(id)}`, { signal })
  },

  suggest(q: string, signal?: AbortSignal) {
    return request<string[] | null>(`${API}/imgb/suggest?q=${encodeURIComponent(q)}`, { signal })
  },

  updateImage(id: string, patch: ImagePatch) {
    return request<ImageDetail>(`${API}/imgb/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },

  deleteImage(id: string) {
    return request<boolean>(`${API}/imgb/${encodeURIComponent(id)}`, { method: 'DELETE' })
  },

  myUploads(cursor?: string, signal?: AbortSignal) {
    const qs = cursor ? `?n=${encodeURIComponent(cursor)}` : ''
    return request<MyUploadsPage>(`${API}/imgb/mine${qs}`, { signal })
  },

  aiProgress(signal?: AbortSignal) {
    return request<AiProgress>(`${API}/admin/ai-progress`, { signal })
  },

  deletionSummary(signal?: AbortSignal) {
    return request<DeletionSummary>(`${API}/admin/deletions/summary`, { signal })
  },

  deletions(
    params: { status: DeletionStatus; reason?: string; cursor?: string; limit?: number },
    signal?: AbortSignal,
  ) {
    const qs = new URLSearchParams({ status: params.status, limit: String(params.limit ?? 60) })
    if (params.reason) qs.set('reason', params.reason)
    if (params.cursor) qs.set('cursor', params.cursor)
    return request<DeletionPage>(`${API}/admin/deletions?${qs}`, { signal })
  },

  decideDeletions(ids: string[], decision: DeletionDecision) {
    return request<{ updated: number }>(`${API}/admin/deletions/decide`, {
      method: 'POST',
      body: JSON.stringify({ ids, decision }),
    })
  },

  decideAllDeletions(reason: string, decision: DeletionDecision) {
    return request<{ updated: number; complete?: boolean }>(`${API}/admin/deletions/decide-all`, {
      method: 'POST',
      body: JSON.stringify({ reason, decision }),
    })
  },

  startCleanup() {
    return request<{ queued: boolean }>(`${API}/admin/deletions/cleanup`, { method: 'POST' })
  },

  bannedTags(signal?: AbortSignal) {
    return request<{ items: BannedTag[] | null }>(`${API}/admin/banned-tags`, { signal })
  },

  banTag(input: BannedTagInput) {
    return request<{ item: BannedTag }>(`${API}/admin/banned-tags`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  unbanTag(tag: string) {
    return request<{ deleted: boolean }>(`${API}/admin/banned-tags/${encodeURIComponent(tag)}`, { method: 'DELETE' })
  },

  async currentUser(): Promise<AuthUser | null> {
    try {
      const data = await request<{ loggedIn: boolean; user: AuthUser | null }>(`${API}/user`)
      return data?.loggedIn && data.user ? data.user : null
    } catch {
      return null
    }
  },

  async exchangeCode(payload: unknown): Promise<boolean> {
    try {
      const res = await fetch(`${API}/auth/exchange`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      return res.ok
    } catch {
      return false
    }
  },
}

export interface UploadInput {
  file: File
  rating: Rating
  tags: string[]
  tagsArtist: string[]
  tagsCharacter: string[]
  tagsCopyright: string[]
  sourceUrl?: string
}

/**
 * Uploads one file. Uses XHR rather than fetch because fetch still has no
 * portable upload progress.
 */
export function uploadImage(
  input: UploadInput,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  const form = new FormData()
  form.set('file', input.file)
  form.set('rating', input.rating)
  form.set('tags', JSON.stringify(input.tags))
  if (input.tagsArtist.length) form.set('tags_artist', JSON.stringify(input.tagsArtist))
  if (input.tagsCharacter.length) form.set('tags_character', JSON.stringify(input.tagsCharacter))
  if (input.tagsCopyright.length) form.set('tags_copyright', JSON.stringify(input.tagsCopyright))
  if (input.sourceUrl) form.set('source_url', input.sourceUrl)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API}/imgb/upload`)
    xhr.withCredentials = true
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.responseType = 'json'

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total)
    }
    xhr.onload = () => {
      try {
        resolve(unwrap<UploadResult>(xhr.status, xhr.status >= 200 && xhr.status < 300, xhr.response))
      } catch (err) {
        reject(err)
      }
    }
    xhr.onerror = () => reject(new ApiError('Network error – check your connection.', 0))
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })

    xhr.send(form)
  })
}
